/**
 * Apply Soft-safe Layer-2 RolePolicy proposals into heal/focus picks.
 * Claim B banned: heal never selects OOR; focus never selects OOR of weapon band.
 */
import { dispositionOf, instinctOf } from "../raid-disposition";
import type { RaidBoss, RaidUnit } from "../raid-types";
import {
  bossBreakWindowOpen,
  unitBreakWindowOpen,
} from "./encounter-break";
import { dpsFocusPolicy, healerKitPolicy } from "./hero-kit-stubs";
import {
  kitForUnit,
  rolePolicyTable,
  unitHasRoleTag,
} from "./hero-registry";
import { legacyToJudgment } from "./legacy-map";
import {
  dpsFocusWireOn,
  healerWireOn,
} from "./role-policy-wire";
import { judgmentNorm } from "./traits";

function judgmentOf(u: RaidUnit) {
  return legacyToJudgment({
    ...dispositionOf(u),
    instinct: instinctOf(u),
  });
}

function dist(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  return Math.hypot(ax - bx, ay - by);
}

function needsHeal(a: RaidUnit, belowPct: number): boolean {
  if (a.hp <= 0) return false;
  if (belowPct >= 1) return a.hp < a.maxHp;
  return a.hp / a.maxHp < belowPct;
}

/** Soft-HP in-staff heal subject via HealerKitPolicy when wire on. */
export function resolveSoftSafeHealNeed(
  healer: RaidUnit,
  allies: readonly RaidUnit[],
  enemies: readonly RaidUnit[],
  belowPct: number,
  fallback: RaidUnit | null,
  time = 0,
): RaidUnit | null {
  if (!healerWireOn() || !unitHasRoleTag(healer, "healer")) return fallback;
  const prop = healerKitPolicy.proposeAction?.({
    hero: healer,
    traits: judgmentOf(healer),
    kit: kitForUnit(healer),
    allies,
    enemies,
    time,
    belowPct,
  });
  if (prop?.subjectId == null) return fallback;
  const sub = allies.find((a) => a.id === prop.subjectId);
  if (!sub || !needsHeal(sub, belowPct)) return fallback;
  if (dist(healer.x, healer.y, sub.x, sub.y) >= healer.attackRange) {
    return fallback;
  }
  return sub;
}

/**
 * Soft-safe focus among already in-band candidate ids (no formation chase).
 * `inBandIds` may include -1 for boss (RAID_AGGRO_BOSS_ID).
 */
export function resolveSoftSafeFocusId(
  hero: RaidUnit,
  boss: RaidBoss | null,
  enemies: readonly RaidUnit[],
  inBandIds: readonly number[],
  time = 0,
): number | null {
  if (!dpsFocusWireOn()) return null;
  if (unitHasRoleTag(hero, "healer")) return null;
  if (inBandIds.length === 0) return null;
  const traits = judgmentOf(hero);
  const insightT = judgmentNorm(traits.insight);
  const bondT = judgmentNorm(traits.bond);
  const rp = rolePolicyTable();
  if (insightT < rp.focusApplyInsightFloor) return null;
  const overrideNeed =
    rp.focusBondOverrideBase + Math.max(0, -bondT) * rp.focusBondOverrideSlope;
  if (insightT < overrideNeed - 0.5) return null;

  const band = new Set(inBandIds);
  const prop = dpsFocusPolicy.proposeAction?.({
    hero,
    traits,
    kit: kitForUnit(hero),
    allies: [],
    enemies,
    time,
    boss,
  });
  if (prop?.subjectId != null && band.has(prop.subjectId)) {
    return prop.subjectId;
  }
  if (boss && bossBreakWindowOpen(boss) && band.has(-1)) {
    return -1;
  }
  for (const id of inBandIds) {
    const e = enemies.find((x) => x.id === id);
    if (e && unitBreakWindowOpen(e) && band.has(id)) return id;
  }
  return null;
}
